/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import englishLanguageTest from '../../../data/selectInputOptions/englishLanguageTest.json';
import frenchLanguageTest from '../../../data/selectInputOptions/frenchLanguageTest.json';
import { getLanguageScoresNewFormat as getScores } from 'utils/options';
import { formCounterSlice } from '../../../redux/formCounter';
import { useDispatch, useSelector } from 'react-redux';
import { FaTrash } from 'react-icons/fa';
import api from 'api';
import ReactGA from 'react-ga4';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import PartnerInfoSwitch from './PartnerInfoSwitch';
import { CustomSlider } from 'components/common/CustomSlider/CustomSlider';
import { useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CEFR_SCALE } from './LanguageFormV2';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { useSpouseTableTarget } from 'hooks/useSpouseUserId';

const LANGUAGE_TEST_FACTORY: { [language: string]: string[] } = {
    '': [''],
    English: englishLanguageTest,
    French: frenchLanguageTest,
};

interface Entry {
    language: string;
    listening: string;
    reading: string;
    speaking: string;
    writing: string;
    test_type: string;
}

const initEntry: Entry = {
    language: '',
    listening: '',
    reading: '',
    speaking: '',
    writing: '',
    test_type: '',
};

interface Msg {
    language: string;
    listening: string;
    reading: string;
    speaking: string;
    writing: string;
    test_type: string;
}

const initMsg: Msg = {
    language: '',
    listening: '',
    reading: '',
    speaking: '',
    writing: '',
    test_type: '',
};

const initialMarks = [
    { value: 0, label: '0' },
    { value: 9, label: '9' },
];

let stepOutside = 1;
const generalStepOutside = 1;

function formatStep(value: number | number[]) {
    if (typeof value === 'number' && value > 6) {
        stepOutside = 0.5;
    } else {
        stepOutside = 1;
    }
}

export default function PartnerLanguageFormV2() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasCanada } = useEnabledCountries();
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    const { setShowSpouse } = formCounterSlice.actions;
    const dispatch = useDispatch();

    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (isSpouseInfo) {
            setIsLoading(false);
        }
    }, [isSpouseInfo]);

    const loadData = (data: Entry[]) => {
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(data);
            fetchScores(data);
        }
        if (data == null || data) {
            setIsSpouseInfo(true);
        } else {
            setIsSpouseInfo(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'PartnerLanguageForm',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const params = useParams();
    const candidate_id = Number(params.id);
    const validCandidateId =
        Number.isFinite(candidate_id) && candidate_id > 0 ? candidate_id : undefined;
    const { tableName, targetUserId, isReady } = useSpouseTableTarget(
        'Language',
        'SpouseLanguage',
        validCandidateId
    );

    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (!isAuthenticated) {
            setIsLoading(false);
            return;
        }
        if (!isReady) {
            return;
        }
        setIsLoading(true);
        setIsSpouseInfo(false);
        setData([]);
        api.tableInput.fetchCandidateInput(loadData, tableName, targetUserId ?? validCandidateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validCandidateId, isAuthenticated, isReady, tableName, targetUserId]);

    const fetchScores = (data: Entry[]) => {
        if (data[0]?.test_type) {
            const populateScore = getMinMaxScores(0, data[0].test_type);
            setScore(populateScore);
            const tempMark = [...mark];
            tempMark[1] = {
                value: populateScore.maxScore,
                label: populateScore.maxScore.toString(),
            };
            setMark(tempMark);
        }
        if (data[1]?.test_type) {
            const populateScore = getMinMaxScores(1, data[1].test_type);
            setSecondScore(populateScore);
            const tempMark = [...secondMark];
            tempMark[1] = {
                value: populateScore.maxScore,
                label: populateScore.maxScore.toString(),
            };
            setSecondMark(tempMark);
        }
    };

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            for (const [key, value] of Object.entries(data[i])) {
                let msg = '';
                if (value === '') {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const [score, setScore] = useState({ minScore: 0, maxScore: 9 });
    const [secondScore, setSecondScore] = useState({ minScore: 0, maxScore: 9 });
    const [mark, setMark] = useState(initialMarks);
    const [secondMark, setSecondMark] = useState(initialMarks);

    const handleUpdate = (idx: number, field: string, value: string) => {
        const temp = [...data];
        if (field === 'language') {
            temp[idx] = {
                ...data[idx],
                [field]: value,
                test_type: '',
                speaking: '',
                listening: '',
                reading: '',
                writing: '',
            };
        } else if (field === 'test_type') {
            temp[idx] = {
                ...data[idx],
                [field]: value,
                speaking: '1',
                listening: '1',
                reading: '1',
                writing: '1',
            };
            if (value) {
                const populateScore = getMinMaxScores(idx, value);
                switch (idx) {
                    case 0: {
                        setScore(populateScore);
                        const tempMark = [...mark];
                        tempMark[1] = {
                            value: populateScore.maxScore,
                            label: populateScore.maxScore.toString(),
                        };
                        setMark(tempMark);
                        break;
                    }
                    case 1: {
                        setSecondScore(populateScore);
                        const tempMark = [...secondMark];
                        tempMark[1] = {
                            value: populateScore.maxScore,
                            label: populateScore.maxScore.toString(),
                        };
                        setSecondMark(tempMark);
                        break;
                    }
                }
            }
        } else {
            temp[idx] = { ...data[idx], [field]: value };
        }
        setData(temp);
    };

    const getMinMaxScores = (id: number, value: string) => {
        const tempScore = getScores(value);

        const tempScoreVal = {
            minScore: Number(tempScore[0]),
            maxScore: Number(tempScore[getScores(value).length - 1]),
        };
        return tempScoreVal;
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = initEntry;

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    function handleSwitchPartnerMe() {
        if (validate()) {
            const writeId = targetUserId ?? validCandidateId;
            if (writeId) {
                api.tableInput.postSales(data, tableName, writeId);
            } else {
                api.tableInput.post(data, tableName);
            }
            dispatch(setShowSpouse());
        }
    }

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox
                    validation={validate}
                    data={[data]}
                    tableNames={[tableName]}
                    targetUserId={targetUserId}
                >
                    <S.TitleContainer>
                        <S.Title>{t('partnerLanguageProficiency', { lng: language })}</S.Title>
                        <PartnerInfoSwitch partnerActive={true} onSwitch={handleSwitchPartnerMe} />
                    </S.TitleContainer>
                    {data.map((_, i) => {
                        return (
                            <S.EntryCard key={i}>
                                <S.LanguageTitle>
                                    <>{`${t('language', { lng: language })}: ${
                                        data[i].language
                                    }`}</>
                                    <S.DeleteButton>
                                        <button onClick={() => handleDeleteEntry(i)}>
                                            <FaTrash />
                                        </button>
                                    </S.DeleteButton>
                                </S.LanguageTitle>
                                <S.FlexColumn>
                                    <S.FlexColumn>
                                        <S.SliderGridSection>
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    id="language"
                                                    value={data[i].language}
                                                    options={
                                                        hasCanada
                                                            ? ['', 'English', 'French']
                                                            : ['', 'English']
                                                    }
                                                    onChange={(_, v: any) => {
                                                        handleUpdate(i, 'language', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('language', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[i].language !== ''}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].test_type}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    id="test_type"
                                                    value={data[i].test_type}
                                                    options={
                                                        LANGUAGE_TEST_FACTORY[data[i].language]
                                                    }
                                                    onChange={(_, v: any) => {
                                                        handleUpdate(i, 'test_type', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('testType', {
                                                                lng: language,
                                                            })}`}
                                                            error={
                                                                validationMsg[i].test_type !== ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].test_type}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>

                                            <S.FlexColumn>
                                                <p>{t('listening', { lng: language })}</p>
                                                <CustomSlider
                                                    id="listening"
                                                    value={parseFloat(data[i].listening || '0')}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            i,
                                                            'listening',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={
                                                        i === 0
                                                            ? score.minScore
                                                            : secondScore.minScore
                                                    }
                                                    max={
                                                        i === 0
                                                            ? score.maxScore
                                                            : secondScore.maxScore
                                                    }
                                                    marks={i === 0 ? mark : secondMark}
                                                    step={
                                                        data[i].test_type === 'IELTS'
                                                            ? stepOutside
                                                            : generalStepOutside
                                                    }
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) =>
                                                        data[i].test_type === 'CEFR'
                                                            ? CEFR_SCALE[value] || value
                                                            : value
                                                    }
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].listening}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('reading', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Reading"
                                                    value={parseFloat(data[i].reading || '0')}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            i,
                                                            'reading',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={
                                                        i === 0
                                                            ? score.minScore
                                                            : secondScore.minScore
                                                    }
                                                    max={
                                                        i === 0
                                                            ? score.maxScore
                                                            : secondScore.maxScore
                                                    }
                                                    marks={i === 0 ? mark : secondMark}
                                                    step={
                                                        data[i].test_type === 'IELTS'
                                                            ? stepOutside
                                                            : generalStepOutside
                                                    }
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) =>
                                                        data[i].test_type === 'CEFR'
                                                            ? CEFR_SCALE[value] || value
                                                            : value
                                                    }
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].reading}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('writing', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Writing"
                                                    value={parseFloat(data[i].writing || '0')}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            i,
                                                            'writing',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={
                                                        i === 0
                                                            ? score.minScore
                                                            : secondScore.minScore
                                                    }
                                                    max={
                                                        i === 0
                                                            ? score.maxScore
                                                            : secondScore.maxScore
                                                    }
                                                    marks={i === 0 ? mark : secondMark}
                                                    step={
                                                        data[i].test_type === 'IELTS'
                                                            ? stepOutside
                                                            : generalStepOutside
                                                    }
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) =>
                                                        data[i].test_type === 'CEFR'
                                                            ? CEFR_SCALE[value] || value
                                                            : value
                                                    }
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].writing}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('speaking', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Speaking"
                                                    value={parseFloat(data[i].speaking || '0')}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            i,
                                                            'speaking',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={
                                                        i === 0
                                                            ? score.minScore
                                                            : secondScore.minScore
                                                    }
                                                    max={
                                                        i === 0
                                                            ? score.maxScore
                                                            : secondScore.maxScore
                                                    }
                                                    marks={i === 0 ? mark : secondMark}
                                                    step={
                                                        data[i].test_type === 'IELTS'
                                                            ? stepOutside
                                                            : generalStepOutside
                                                    }
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) =>
                                                        data[i].test_type === 'CEFR'
                                                            ? CEFR_SCALE[value] || value
                                                            : value
                                                    }
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].speaking}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        </S.SliderGridSection>
                                    </S.FlexColumn>
                                </S.FlexColumn>
                            </S.EntryCard>
                        );
                    })}
                    <AddEntryFooter
                        title={t('addLanguage', { lng: language })}
                        addLabel={t('addLanguage', { lng: language })}
                        addAnotherLabel={t('addAnotherLanguage', { lng: language })}
                        hasEntries={data.length > 0}
                        onClick={handleAddEntry}
                    />
                </FormBox>
            )}
        </>
    );
}
